import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const BUTTON_3D_BASE = {
  transformStyle: 'preserve-3d',
  transform: 'translateZ(0)',
  transition: 'transform 180ms cubic-bezier(.22,1,.36,1), box-shadow 180ms ease',
};

function threeDButton(base, isLight, hover, pressed) {
  const depth = pressed ? 1 : hover ? 4 : 2;
  const offset = pressed ? 0 : hover ? 4 : 2;
  const shadowTop = isLight
    ? 'rgba(255,255,255,0.9)'
    : 'rgba(255,255,255,0.12)';
  const shadowBottom = isLight
    ? 'rgba(15,23,42,0.22)'
    : 'rgba(0,0,0,0.65)';
  return {
    ...base,
    transform: `translateZ(0) translateY(-${offset}px) ${pressed ? 'scale(0.97)' : hover ? 'scale(1.03)' : ''}`,
    boxShadow: `inset 0 1px 0 ${shadowTop}, 0 ${depth}px 0 ${shadowBottom}, 0 ${
      depth + 6
    }px 14px -6px ${isLight ? 'rgba(15,23,42,0.28)' : 'rgba(0,0,0,0.55)'}`,
  };
}

export default function LinesPanel({
  lines,
  isOpen,
  onToggle,
  onToggleVisibility,
  onDeleteLine,
  onZoomToLine,
  onEditTag,
  isLight = false,
  C = null,
}) {
  const theme = C || (isLight
    ? {
        accent: '#2563eb',
        accentSoft: 'rgba(37, 99, 235, 0.75)',
        accentBg: 'rgba(37, 99, 235, 0.10)',
        accentBgHover: 'rgba(37, 99, 235, 0.14)',
        accentOnBg: '#ffffff',
        glassBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.8) 50%, rgba(239, 246, 255, 0.85) 100%)',
        glassInnerBg: 'linear-gradient(135deg, rgba(239, 246, 255, 0.7) 0%, rgba(241, 245, 249, 0.7) 100%)',
        glassBorder: 'rgba(37, 99, 235, 0.18)',
        glassInnerBorder: 'rgba(148, 163, 184, 0.20)',
        glassShadow: '0 4px 16px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        textPrimary: '#0f172a',
        textSecondary: 'rgba(51, 65, 85, 0.75)',
        textMuted: 'rgba(100, 116, 139, 0.65)',
        btnGhostBg: 'rgba(15, 23, 42, 0.05)',
        btnGhostBgHover: 'rgba(15, 23, 42, 0.09)',
        btnGhostBorder: 'rgba(15, 23, 42, 0.10)',
        divider: 'rgba(15, 23, 42, 0.10)',
        inputBg: 'rgba(255, 255, 255, 0.9)',
        inputBorder: 'rgba(148, 163, 184, 0.30)',
      }
    : {
        accent: '#facc15',
        accentSoft: 'rgba(250, 204, 21, 0.7)',
        accentBg: 'rgba(250, 204, 21, 0.12)',
        accentBgHover: 'rgba(250, 204, 21, 0.16)',
        accentOnBg: '#0f172a',
        glassBg: 'linear-gradient(135deg, rgba(30, 58, 138, 0.55) 0%, rgba(23, 37, 84, 0.5) 50%, rgba(30, 64, 175, 0.55) 100%)',
        glassInnerBg: 'linear-gradient(135deg, rgba(30, 64, 175, 0.35) 0%, rgba(23, 37, 84, 0.3) 100%)',
        glassBorder: 'rgba(250, 204, 21, 0.18)',
        glassInnerBorder: 'rgba(96, 165, 250, 0.14)',
        glassShadow: '0 8px 32px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
        textPrimary: '#ffffff',
        textSecondary: 'rgba(226, 232, 240, 0.7)',
        textMuted: 'rgba(148, 163, 184, 0.6)',
        btnGhostBg: 'rgba(255, 255, 255, 0.10)',
        btnGhostBgHover: 'rgba(255, 255, 255, 0.15)',
        btnGhostBorder: 'rgba(255, 255, 255, 0.15)',
        divider: 'rgba(255, 255, 255, 0.10)',
        inputBg: 'rgba(255, 255, 255, 0.10)',
        inputBorder: 'rgba(255, 255, 255, 0.15)',
      });

  const glassCardStyle = {
    background: theme.glassBg,
    border: `1px solid ${theme.glassBorder}`,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: theme.glassShadow,
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draftTag, setDraftTag] = useState('');

  const openEdit = (line) => {
    setEditingId(line.id);
    setDraftTag(line.tag || '');
  };

  const closeEdit = () => {
    setEditingId(null);
    setDraftTag('');
  };

  const saveEdit = (lineId) => {
    onEditTag(lineId, draftTag);
    closeEdit();
  };

  const confirmDeleteLine = (line) => {
    setConfirmDelete(line);
  };

  const cancelDelete = () => setConfirmDelete(null);

  const doDelete = () => {
    if (confirmDelete) {
      onDeleteLine(confirmDelete.id);
    }
    setConfirmDelete(null);
  };

  return (
    <>
      <LinesToggleButton
        count={lines.length}
        onClick={onToggle}
        isLight={isLight}
        theme={theme}
      />

      {isOpen && (
        <div
          className="absolute top-16 right-4 z-[500] rounded-xl w-80 max-h-[calc(100%-6rem)] flex flex-col slide-in"
          style={glassCardStyle}
        >
          <div
            className="p-4 border-b flex items-center justify-between"
            style={{ borderColor: theme.divider }}
          >
            <h3 className="font-bold text-lg" style={{ color: theme.accent }}>Places</h3>
          </div>

          <div className="overflow-y-auto flex-1 p-2">
            {lines.length === 0 ? (
              <div className="text-sm text-center py-8 px-4" style={{ color: theme.textMuted }}>
                <svg
                  className="w-12 h-12 mx-auto mb-3 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: theme.textMuted }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                No places drawn yet. Hit Start Drawing and click the map to begin.
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {lines.map((line) => (
                  <LineItem
                    key={line.id}
                    line={line}
                    onToggleVisibility={onToggleVisibility}
                    onConfirmDelete={confirmDeleteLine}
                    onZoomTo={onZoomToLine}
                    isEditing={editingId === line.id}
                    draftTag={draftTag}
                    onDraftChange={setDraftTag}
                    onOpenEdit={openEdit}
                    onCancelEdit={closeEdit}
                    onSaveEdit={saveEdit}
                    theme={theme}
                    isLight={isLight}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AnimatePresence>
        {confirmDelete && (
          <DeleteConfirmDialog
            line={confirmDelete}
            onCancel={cancelDelete}
            onConfirm={doDelete}
            isLight={isLight}
            theme={theme}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function LinesToggleButton({ count, onClick, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      data-testid="mappoint-lines-toggle"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="absolute top-4 right-4 z-[500] px-4 py-2.5 rounded-xl font-semibold text-sm border flex items-center gap-2"
      style={{
        background: hover ? theme.btnGhostBgHover : theme.btnGhostBg,
        borderColor: theme.btnGhostBorder,
        color: theme.accent,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      Places ({count})
    </button>
  );
}

function LineItem({
  line,
  onToggleVisibility,
  onConfirmDelete,
  onZoomTo,
  isEditing,
  draftTag,
  onDraftChange,
  onOpenEdit,
  onCancelEdit,
  onSaveEdit,
  theme,
  isLight,
}) {
  const itemBg = line.visible
    ? { background: theme.glassInnerBg, border: `1px solid ${theme.glassInnerBorder}`, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }
    : { backgroundColor: theme.btnGhostBg, border: `1px solid ${theme.btnGhostBorder}`, opacity: 0.5 };

  return (
    <div className="p-3 rounded-lg transition-all" style={itemBg}>
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 rounded-full flex-shrink-0"
          style={{ backgroundColor: line.color, height: `${Math.max(line.weight, 3)}px` }}
        />
        {isEditing ? (
          <input
            type="text"
            autoFocus
            value={draftTag}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="No tag"
            className="flex-1 text-sm font-medium min-w-0 px-2 py-1 rounded-md outline-none"
            style={{
              color: theme.textPrimary,
              backgroundColor: theme.inputBg,
              border: `1px solid ${theme.accent}99`,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit(line.id);
              if (e.key === 'Escape') onCancelEdit();
            }}
          />
        ) : (
          <div
            className="flex-1 text-sm font-medium min-w-0 truncate"
            style={{ color: theme.textPrimary }}
            title={line.tag || 'No tag'}
          >
            {line.tag || <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>No tag</span>}
          </div>
        )}
        <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
          {line.points.length} pts
        </span>
      </div>

      <div className="flex gap-1">
        <ActionButton
          onClick={() => onZoomTo(line)}
          title="Zoom to line"
          isLight={isLight}
          theme={theme}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
          Zoom
        </ActionButton>
        <ActionButton
          onClick={() => onToggleVisibility(line.id)}
          title={line.visible ? 'Hide line' : 'Show line'}
          isLight={isLight}
          theme={theme}
        >
          {line.visible ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          )}
          {line.visible ? 'Hide' : 'Show'}
        </ActionButton>

        {isEditing ? (
          <>
            <PrimaryButton
              onClick={() => onSaveEdit(line.id)}
              title="Save tag"
              accent
              isLight={isLight}
              theme={theme}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Save
            </PrimaryButton>
            <DangerButton
              onClick={onCancelEdit}
              title="Cancel edit"
              isLight={isLight}
              theme={theme}
              soft
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </DangerButton>
          </>
        ) : (
          <>
            <ActionButton
              onClick={() => onOpenEdit(line)}
              title="Edit this place"
              isLight={isLight}
              theme={theme}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </ActionButton>
            <DeleteButton
              onClick={() => onConfirmDelete(line)}
              title="Delete line"
              isLight={isLight}
              theme={theme}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ActionButton({ onClick, title, children, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const color = hover ? theme.accent : theme.textSecondary;
  const bg = hover ? theme.btnGhostBgHover : 'transparent';
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 text-xs py-1.5 rounded flex items-center justify-center gap-1 border border-transparent"
      style={{
        backgroundColor: bg,
        color,
        ...threeDButton({ transition: BUTTON_3D_BASE.transition }, isLight, hover, pressed),
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ onClick, title, children, isLight, theme, accent }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 text-xs py-1.5 rounded flex items-center justify-center gap-1 border border-transparent font-semibold"
      style={{
        backgroundColor: accent ? theme.accent + 'cc' : (hover ? theme.btnGhostBgHover : theme.btnGhostBg),
        color: accent ? theme.accentOnBg : (hover ? theme.accent : theme.textSecondary),
        ...threeDButton({ transition: BUTTON_3D_BASE.transition }, isLight, hover, pressed),
      }}
    >
      {children}
    </button>
  );
}

function DangerButton({ onClick, title, children, isLight, theme, soft }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = hover
    ? (isLight ? 'rgba(239,68,68,0.14)' : 'rgba(239,68,68,0.25)')
    : soft
      ? 'transparent'
      : (isLight ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.15)');
  const color = hover
    ? (isLight ? '#dc2626' : '#fca5a5')
    : (isLight ? 'rgba(220, 38, 38, 0.7)' : 'rgba(248, 113, 113, 0.7)');
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 text-xs py-1.5 rounded flex items-center justify-center gap-1 border border-transparent"
      style={{
        backgroundColor: bg,
        color,
        ...threeDButton({ transition: BUTTON_3D_BASE.transition }, isLight, hover, pressed),
      }}
    >
      {children}
    </button>
  );
}

function DeleteButton({ onClick, title, isLight, theme }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = hover
    ? (isLight ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.15)')
    : 'transparent';
  const color = hover
    ? (isLight ? '#dc2626' : '#fca5a5')
    : (isLight ? 'rgba(220, 38, 38, 0.7)' : 'rgba(248, 113, 113, 0.7)');
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="relative flex-1 text-xs py-1.5 rounded flex items-center justify-center gap-1 border border-transparent"
      style={{
        backgroundColor: bg,
        color,
        ...threeDButton({ transition: BUTTON_3D_BASE.transition }, isLight, hover, pressed),
      }}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
      Delete
    </button>
  );
}

function DeleteConfirmDialog({ line, onCancel, onConfirm, isLight, theme }) {
  const panelBg = isLight ? 'rgba(255,255,255,0.98)' : 'rgba(14,15,19,0.98)';
  const panelBorder = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.12)';
  const panelShadow = isLight
    ? '0 32px 80px -16px rgba(15,23,42,0.45), inset 0 1px 0 rgba(255,255,255,0.95)'
    : '0 32px 80px -16px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.12)';

  return (
    <motion.div
      key="delete-confirm-backdrop"
      className="fixed inset-0 z-[1000] grid place-items-center px-4"
      initial={{ backgroundColor: 'rgba(0,0,0,0)' }}
      animate={{ backgroundColor: isLight ? 'rgba(15,23,42,0.35)' : 'rgba(0,0,0,0.55)' }}
      exit={{ backgroundColor: 'rgba(0,0,0,0)' }}
      transition={{ duration: 0.2 }}
      onMouseDown={onCancel}
    >
      <motion.div
        key="delete-confirm-panel"
        className="relative rounded-2xl border p-5 w-full max-w-sm"
        initial={{ opacity: 0, y: 12, scale: 0.96, filter: 'blur(6px)' }}
        animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: 6, scale: 0.98, filter: 'blur(4px)' }}
        transition={{ type: 'spring', stiffness: 260, damping: 24, mass: 0.78 }}
        style={{
          background: panelBg,
          borderColor: panelBorder,
          boxShadow: panelShadow,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{
              backgroundColor: isLight ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.22)',
              color: isLight ? '#dc2626' : '#fca5a5',
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4
              className="font-bold text-base mb-1"
              style={{ color: theme.textPrimary }}
            >
              Delete this place?
            </h4>
            <p className="text-xs mb-4" style={{ color: theme.textSecondary }}>
              The route{' '}
              <span className="font-semibold" style={{ color: theme.textPrimary }}>
                "{line.tag || 'Untitled'}"
              </span>{' '}
              with <span className="font-semibold">{line.points.length} points</span> will be permanently removed. This cannot be undone.
            </p>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="relative px-4 py-2 rounded-lg text-xs font-semibold border"
            style={{
              backgroundColor: theme.btnGhostBg,
              borderColor: theme.btnGhostBorder,
              color: theme.textSecondary,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.btnGhostBgHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.btnGhostBg)}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="relative px-4 py-2 rounded-lg text-xs font-semibold border border-transparent"
            style={{
              backgroundColor: isLight ? '#dc2626' : '#ef4444',
              color: '#ffffff',
              boxShadow: isLight
                ? 'inset 0 1px 0 rgba(255,255,255,0.35), 0 2px 0 rgba(153,27,27,0.9), 0 10px 20px -8px rgba(239,68,68,0.55)'
                : 'inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 0 rgba(127,29,29,0.9), 0 10px 20px -8px rgba(239,68,68,0.55)',
            }}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
